import React from 'react';

import { IPerformanceHeroFields } from '../../../types/contentful';

import { usePageDataContext } from '../../context/PageData';
import { getImageDataFromOptions } from '../../utils/contentful';

import CtaWidget from '../CtaWidget';
import { VideoPlayer } from '../VideoPlayer';
import PrivacyPolicyDisclaimer from '../PrivacyPolicyDisclaimer';

import { TextWithOptions } from '../Text/TextWithOptions';
import GlImage from '../Image';
import { Asset } from 'contentful';
import { useMediaQuery } from '~/hooks/useMediaQuery';

export function PerformanceHero({
  headingText,
  headingTextOptions,
  subheadingText,
  subheadingTextOptions,
  image,
  imageOptions,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  desktopCta,
  mobileCta,
  videoModule
}: IPerformanceHeroFields) {
  const visibility = image ? 'visible' : 'hidden';
  const {
    cookies: { utm_source = '' } = {},
    query: { gl_content, g_adgroupid } = {}
  } = usePageDataContext();

  const isMobile = !useMediaQuery('desktop');

  return (
    <>
      {!isMobile && (
        <section
          data-blockid="performance-hero-desktop"
          className="desktop-block-container mt-container tablet:mt-3"
          data-e2e="hero"
          data-testid="performance-hero-desktop"
        >
          <div className="layout layout-padding mt-2 box-border w-full flex-1 items-center justify-center">
            <div className="flex w-full items-center justify-center gap-8 px-15 py-8">
              <div className="flex w-full flex-1 flex-col items-center">
                <div className="box-border flex flex-col items-start justify-center gap-10">
                  <div className="flex max-w-[532px] flex-col items-start gap-5">
                    {headingText && (
                      <TextWithOptions
                        as="h1"
                        v2
                        className="self-stretch text-left text-[52px] leading-[115%]"
                        keyword={gl_content}
                        channel={utm_source}
                        adGroupId={g_adgroupid}
                        fallback={headingText}
                        options={headingTextOptions}
                        module="Performance Hero heading"
                        data-testid="performance-hero-headline-desktop"
                      />
                    )}
                    {subheadingText && (
                      <TextWithOptions
                        as="p"
                        v2
                        className="self-stretch text-left text-[20px] leading-[32px]"
                        data-testid="performance-hero-subhead-desktop"
                        keyword={gl_content}
                        channel={utm_source}
                        adGroupId={g_adgroupid}
                        fallback={subheadingText}
                        options={subheadingTextOptions}
                        module="Performance Hero subheading"
                      />
                    )}
                  </div>
                  <div className="flex max-w-[350px] flex-col items-start gap-3 p-0">
                    {desktopCta && (
                      <CtaWidget
                        {...desktopCta?.fields}
                        sysId={desktopCta?.sys?.contentType?.sys?.id}
                        id="performance-desktop-hero-cta"
                        module="Hero"
                        height={56}
                        isMobile={false}
                        responsive
                        width={'100%'}
                      />
                    )}
                    {privacyPolicyDisclaimer && (
                      <div className="box-border">
                        <PrivacyPolicyDisclaimer
                          privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                          displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-1 flex-col items-center gap-3">
                {videoModule?.fields?.vimeoId ? (
                  <VideoPlayer {...videoModule?.fields} />
                ) : (
                  <div className="relative box-border flex w-full flex-1 flex-col gap-3">
                    <GlImage
                      className="h-full max-h-[630px] max-w-[630px] rounded-4xl"
                      {...getImageDataFromOptions(
                        gl_content,
                        utm_source,
                        g_adgroupid,
                        image as Asset,
                        imageOptions
                      )}
                      loading="eager"
                      heroImage={true}
                      style={{ visibility }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
      {isMobile && (
        <section
          className="block-container flex-col items-center desktop:hidden"
          data-testid="performance-hero-mobile"
        >
          <div className="flex flex-col items-center justify-center self-stretch tablet:gap-6 tablet:px-10 tablet:pt-8">
            <div className="flex flex-col items-center justify-center gap-3 self-stretch">
              <div className="w-full self-stretch">
                {videoModule?.fields?.vimeoId ? (
                  <VideoPlayer {...videoModule?.fields} rounded={false} />
                ) : (
                  <GlImage
                    {...getImageDataFromOptions(
                      gl_content,
                      utm_source,
                      g_adgroupid,
                      image,
                      imageOptions
                    )}
                    heroImage={true}
                    loading="eager"
                    data-testid="fullwidth-hero-mobile-background"
                  />
                )}
              </div>
            </div>
            <div className="mb-4 flex flex-col items-center justify-center gap-6 self-stretch px-5 pt-8 tablet:mb-0 tablet:justify-normal tablet:gap-4 tablet:p-0">
              {headingText && (
                <TextWithOptions
                  as="h2"
                  v2
                  className="text-center text-[40px] font-medium leading-[115%] tablet:text-[52px]"
                  keyword={gl_content}
                  channel={utm_source}
                  adGroupId={g_adgroupid}
                  fallback={headingText}
                  options={headingTextOptions}
                  data-testid={'performance-hero-headline-mobile'}
                  module="Performance Hero mobile heading"
                />
              )}
              {subheadingText && (
                <TextWithOptions
                  as="p"
                  v2
                  className="text-center text-lg leading-[115%]"
                  data-testid="performance-hero-subheading-mobile"
                  keyword={gl_content}
                  channel={utm_source}
                  adGroupId={g_adgroupid}
                  fallback={subheadingText}
                  options={subheadingTextOptions}
                  module="Performance Hero mobile subheading"
                />
              )}
            </div>
            <div className="flex max-w-[350px] flex-col items-start gap-3 p-0">
              {mobileCta && (
                <CtaWidget
                  {...mobileCta?.fields}
                  id="fullwidth-hero-mobile-cta"
                  sysId={mobileCta?.sys?.contentType?.sys?.id}
                  module="Hero"
                  isMobile={true}
                  width={'100%'}
                  responsive
                />
              )}
              <div className="box-border">
                <PrivacyPolicyDisclaimer
                  privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                  displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                />
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
